import { useEffect, useMemo, useRef, useState } from "react";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import { Textarea, Tooltip } from "rizzui";
import { toast } from "sonner";
import CustomSelect from "../../../components/forms/CustomSelect";
import CustomSelectApiHookForm from "../../../components/forms/CustomSelectApiHookForms";
import RequiredInput from "../../../components/forms/InputHOC";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useContractCampaign from "../../../hooks/api-calls/contract/useContractCampaign";
import useContractCommercial from "../../../hooks/api-calls/contract/useContractCommercial";
import useExpirationTypes from "../../../hooks/api-calls/contract/useExpirationTypes";
import usePaymentMethods from "../../../hooks/api-calls/contract/usePaymentMethods";
import useCorporations from "../../../hooks/api-calls/useCorporations";
import useContractUserNetworks from "../../../hooks/api-calls/useContractUserNetworks";
import FormGroup from "../../../layout/shared/form-group";
import cn from "../../../utils/classNames";

interface ContractDataFormProps {
    formik: any;
    setCommercialTransferData: any;
    isCreateMode?: boolean;
}

export const contractTypeOptions = [
    { value: 'recruitment', label: 'Captación' },
    { value: 'renovation', label: 'Renovación' },
];

const getCommercialDisplayName = (label?: string | null) => label?.replace(/\s+\([^)]*\)$/, '') ?? '';

const ContractDataForm = ({ formik, setCommercialTransferData, isCreateMode = false }: ContractDataFormProps) => {

    const { userCan } = usePrivilege();
    const { getContractCampaignList } = useContractCampaign({ active: true });
    const { getContractCommercialList } = useContractCommercial({ active: true });
    const { fetchCorporations: fetchOrganisingAndServicesCorporations } = useCorporations({ active: true, corporation_functions: ['organising', 'services'] });
    const { fetchCorporations: fetchProvidingCorporations } = useCorporations({ active: true, corporation_function: 'providing' });
    const { fetchCorporations: fetchPlatformCorporations } = useCorporations({ active: true, corporation_function: 'platform' });
    const { getUserNetworksList } = useContractUserNetworks({ active: true });
    const { getPaymentMethodsList } = usePaymentMethods();
    const { getExpirationTypesList } = useExpirationTypes();

    const [expirationType, setExpirationType] = useState<string | null>(null);
    const [organisingCorporationLocked, setOrganisingCorporationLocked] = useState(false);
    const previousCommercialId = useRef<string>(formik.values.commercialId ?? '');
    const contractCommercialList = getContractCommercialList();

    /**
     * Stable formik-like object for the combined organising/services selector.
     * Only recalculates when the two relevant values change, preventing unnecessary
     * re-fetches caused by new object references on every render.
     */
    const organisingServicesFormik = useMemo(() => ({
        ...formik,
        values: {
            ...formik.values,
            organisingCorporationId: formik.values.servicesCorporationId || formik.values.organisingCorporationId,
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [formik.values.servicesCorporationId, formik.values.organisingCorporationId]);

    /**
     * When commercial network has exactly 1 organising corporation: auto-assign and lock the field.
     * When network changes, clear organising and providing corporation.
     */
    const isFirstCommercialNetworkRender = useRef(true);
    useEffect(() => {
        if (isFirstCommercialNetworkRender.current) {
            isFirstCommercialNetworkRender.current = false;
            return;
        }
        formik.setFieldValue('organisingCorporationId', '');
        formik.setFieldValue('providingCorporationId', '');
        formik.setFieldValue('platformServiceCorporationId', '');
        formik.setFieldValue('servicesCorporationId', '');
    }, [formik.values.commercialNetworkId]);

    useEffect(() => {
        if (!formik.values.commercialNetworkId) {
            setOrganisingCorporationLocked(false);
            return;
        }
        const fetchAndCheck = async () => {
            try {
                const result = await fetchOrganisingAndServicesCorporations({
                    commercial_network_id: formik.values.commercialNetworkId,
                    search_text: '',
                }) as any;
                if (Array.isArray(result) && result.length === 1) {
                    const corp = result[0];
                    if (corp.corporationFunction === 'services') {
                        formik.setFieldValue('servicesCorporationId', corp.value);
                        formik.setFieldValue('organisingCorporationId', '');
                        formik.setFieldValue('providingCorporationId', '');
                        formik.setFieldValue('platformServiceCorporationId', '');
                    } else {
                        formik.setFieldValue('organisingCorporationId', corp.value);
                        formik.setFieldValue('servicesCorporationId', '');
                    }
                    setOrganisingCorporationLocked(true);
                } else {
                    setOrganisingCorporationLocked(false);
                }
            } catch {
                setOrganisingCorporationLocked(false);
            }
        };
        fetchAndCheck();
    }, [formik.values.commercialNetworkId]);

    /**
     * Set legalRepresentativeExist to true if legalRepresentativeName or legalRepresentativeNif is not empty
     */
    useEffect(() => {
        if (formik.values.legalRepresentativeName || formik.values.legalRepresentativeNif) {
            formik.setFieldValue('legalRepresentativeExist', true);
        } else {
            formik.setFieldValue('legalRepresentativeExist', false);
        }
    }, [formik.values.legalRepresentativeName, formik.values.legalRepresentativeNif]);

    /**
     * Auto-asignar commercialNetworkId cuando el usuario tiene exactamente 1 red y el campo está vacío.
     * Necesario para borradores: el campo puede estar oculto pero es obligatorio.
     */
    const networksList = getUserNetworksList();
    useEffect(() => {
        if (networksList?.length === 1 && !formik.values.commercialNetworkId) {
            formik.setFieldValue('commercialNetworkId', networksList[0]?.value ?? '');
        }
    }, [networksList?.length, formik.values.commercialNetworkId]);

    useEffect(() => {
        if (!formik.values.commercialId) {
            previousCommercialId.current = '';
            return;
        }

        const selectedCommercial = contractCommercialList?.find((commercial: any) => commercial.value === formik.values.commercialId);
        const selectedCommercialName = getCommercialDisplayName(selectedCommercial?.label);
        if (!selectedCommercialName) return;

        const commercialChanged = previousCommercialId.current !== formik.values.commercialId;
        const operatorNameIsEmpty = !formik.values.operatorName;

        if (commercialChanged || operatorNameIsEmpty) {
            formik.setFieldValue('operatorName', selectedCommercialName);
            setCommercialTransferData((prevData: any) => ({
                ...prevData,
                newCommercial: selectedCommercialName
            }));
        }

        previousCommercialId.current = formik.values.commercialId;
    }, [formik.values.commercialId, contractCommercialList.length]);

    /**
     * When organisingCorporationId changes: clear providing (except on first load), then fetch and auto-assign if exactly 1 impartidora.
     */
    const isFirstOrganisingRender = useRef(true);
    useEffect(() => {
        if (!formik.values.organisingCorporationId) {
            formik.setFieldValue('providingCorporationId', '');
            formik.setFieldValue('platformServiceCorporationId', '');
            return;
        }
        if (isFirstOrganisingRender.current) {
            isFirstOrganisingRender.current = false;
        } else {
            formik.setFieldValue('providingCorporationId', '');
            formik.setFieldValue('platformServiceCorporationId', '');
            // Selecting organising clears services (mutual exclusion).
            // Note: if this was triggered by the combined selector choosing a services corp,
            // the setValue callback will correct organisingCorporationId right after.
        }
        const fetchAndAssign = async () => {
            try {
                const result = await fetchProvidingCorporations({
                    organising_corporation_id: formik.values.organisingCorporationId,
                    search_text: '',
                }) as any;
                const list = Array.isArray(result) ? result : (result?.data ?? []);
                if (list.length >= 1) {
                    const id = list[0].value ?? list[0].id;
                    if (id) formik.setFieldValue('providingCorporationId', id);
                }
            } catch {
                // ignore
            }
        };
        fetchAndAssign();
    }, [formik.values.organisingCorporationId]);

    useEffect(() => {
        if (!formik.values.providingCorporationId) {
            formik.setFieldValue('platformServiceCorporationId', '');
        }
    }, [formik.values.providingCorporationId]);

    // When a services corporation is selected, clear and lock providing + platform fields
    useEffect(() => {
        if (formik.values.servicesCorporationId) {
            if (formik.values.providingCorporationId) {
                formik.setFieldValue('providingCorporationId', '');
            }
            if (formik.values.platformServiceCorporationId) {
                formik.setFieldValue('platformServiceCorporationId', '');
            }
        }
    }, [formik.values.servicesCorporationId]);

    /**
     * Set expirationTypeId value based on social insurance amount and total amount
     */
    useEffect(() => {
        const list = getExpirationTypesList();
        if (list.length > 0 && formik.values.socialInsuranceAmount != null && formik.values.totalAmount != null && formik.values.totalAmount > 0) {
            let newExpirationTypeId: string | null = null;
            let newPaymentInstalmentsNumber = 1;
            let newExpirationType: string | null = null;
            let toastMessage: string | null = null;

            if (formik.values.socialInsuranceAmount > formik.values.totalAmount) {
                newExpirationType = 'full_direct_debiting';
                newExpirationTypeId = list.find((item: any) => item.identifierLabel === 'full_direct_debiting')?.value ?? null;
                newPaymentInstalmentsNumber = 1;
                toastMessage = 'Se ha cambiado el tipo de vencimiento a "Domiciliación íntegra"';
            } else if (((formik.values.socialInsuranceAmount * 100) / formik.values.totalAmount) >= 50) {
                newExpirationType = 'instalment_payment';
                newExpirationTypeId = list.find((item: any) => item.identifierLabel === 'instalment_payment')?.value ?? null;
                newPaymentInstalmentsNumber = 2;
                toastMessage = 'Se ha cambiado el tipo de vencimiento a "Fraccionado"';
            } else if (formik.values.totalAmount > (formik.values.socialInsuranceAmount * 2)) {
                newExpirationType = 'credit_balance';
                newExpirationTypeId = list.find((item: any) => item.identifierLabel === 'credit_balance')?.value ?? null;
                newPaymentInstalmentsNumber = 2;
                toastMessage = 'Se ha cambiado el tipo de vencimiento a "Saldo acreedor"';
            }

            if (newExpirationTypeId != null && formik.values.expirationTypeId !== newExpirationTypeId) {
                setExpirationType(newExpirationType);
                formik.setFieldValue('expirationTypeId', newExpirationTypeId);
                formik.setFieldValue('paymentInstalmentsNumber', newPaymentInstalmentsNumber);
                if (toastMessage) toast.info(toastMessage);
            }
        }
    }, [getExpirationTypesList, formik.values.socialInsuranceAmount, formik.values.totalAmount]);

    return (
        <div className="rounded-md border-solid border-2 col-span-7 p-6 divide-y-2 divide-dashed divide-gray-200 gap-7">
            <div className='flex flex-col md:flex-row md:justify-between items-center'>
                <h3 className='font-bold text-lg pb-2'>Datos Generales del Contrato</h3>
            </div>

            <FormGroup
                title="Información General"
                description="Datos asociados al contrato."
                className={cn("py-4 @3xl:grid-cols-12 ")}
                titleCols="@md:col-span-2"
                childCols="@md:col-span-10 md:grid-cols-4"
            >
                <div className="grid grid-cols-12 col-span-8 gap-4">
                    <CustomSelect
                        isSearchable
                        isClearable
                        id={'contractType'}
                        label={
                            <span className="flex items-center">
                                Tipo de Contrato
                                <Tooltip
                                    size="md"
                                    content={'La selección será automatica si no se indica el tipo de contrato.'}
                                    placement="top"
                                    color="invert"
                                >
                                    <div className={'inline-block ms-1'}>
                                        <HiOutlineInformationCircle size={20} color={'primary'} />
                                    </div>

                                </Tooltip>
                            </span>
                        }
                        containerClassName="col-span-12 md:col-span-4"
                        value={formik.values.contractType ? { value: formik.values.contractType, label: contractTypeOptions.find((g: any) => g.value === formik.values.contractType)?.label } : null}
                        options={contractTypeOptions}
                        onChange={(e: any) => { formik.setFieldValue('contractType', e?.value ?? ''); }}
                        error={formik.errors.contractType}
                        required={false}
                    />

                    <CustomSelect
                        isSearchable
                        isClearable
                        id={'campaignId'}
                        label="Campaña"
                        containerClassName="col-span-12 md:col-span-4"
                        value={formik.values.campaignId ? { value: formik.values.campaignId, label: getContractCampaignList()?.find((g: any) => g.value === formik.values.campaignId)?.label } : null}
                        options={getContractCampaignList()}
                        onChange={(e: any) => { formik.setFieldValue('campaignId', e?.value ?? ''); }}
                        error={formik.errors.campaignId}
                        required={false}
                    />

                    {userCan('admin_all_commercial_network_contracts', 'commercial_network') && (
                        <CustomSelect
                            isSearchable
                            isClearable
                            id="commercialId"
                            label="Comercial"
                            containerClassName="col-span-12 md:col-span-4"
                            value={formik.values.commercialId ? { value: formik.values.commercialId, label: getContractCommercialList()?.find((g: any) => g.value === formik.values.commercialId)?.label } : null}
                            options={contractCommercialList}
                            onChange={(e: any) => {
                                formik.setFieldValue('commercialId', e?.value ?? '');
                                if (e?.label) {
                                    formik.setFieldValue('operatorName', getCommercialDisplayName(e.label));
                                }
                                setCommercialTransferData((prevData: any) => ({
                                    ...prevData,
                                    newCommercial: getCommercialDisplayName(e?.label)
                                }));
                            }}
                            error={formik.errors.commercialId}
                        />
                    )}

                    {userCan('admin_all_commercial_network_contracts', 'commercial_network') && (
                        <CustomSelect
                            required={true}
                            isSearchable
                            isClearable
                            id={'commercialNetworkId'}
                            label="Red Comercial"
                            containerClassName="col-span-12 md:col-span-4"
                            value={formik.values.commercialNetworkId ? { value: formik.values.commercialNetworkId, label: getUserNetworksList()?.find((g: any) => g.value === formik.values.commercialNetworkId)?.label } : null}
                            options={getUserNetworksList()}
                            onChange={(e: any) => { formik.setFieldValue('commercialNetworkId', e?.value ?? ''); }}
                            error={formik.errors.commercialNetworkId}
                            className={cn(!userCan('admin_all_commercial_network_contracts', 'contracts')) && 'hidden'}
                        />
                    )}

                    <CustomSelectApiHookForm
                        key={formik.values.commercialNetworkId + 'organising-services-key'}
                        id="organisingCorporationId"
                        label={<span>Empresa Organizadora / Servicios {(formik.values.saveAsContract && formik.values.productTypeId === '26a41eb6-a9d1-4d2f-88e5-1bb59bdb9ae1') && <span className="text-red-500">*</span>}</span>}
                        formik={organisingServicesFormik}
                        fetchOptions={async (searchText: string | null) => {
                            const filters: any = { search_text: searchText, corporation_functions: ['organising', 'services'] };
                            if (formik.values.commercialNetworkId) {
                                filters.commercial_network_id = formik.values.commercialNetworkId;
                            }
                            return fetchOrganisingAndServicesCorporations(filters) as any;
                        }}
                        setValue={(selected: any) => {
                            // Route to correct formik field based on corporationFunction from option
                            const option = selected?.object;
                            if (!option || !selected.id) {
                                console.log('selected', selected);
                                formik.setFieldValue('organisingCorporationId', '');
                                formik.setFieldValue('servicesCorporationId', '');
                                formik.setFieldValue('providingCorporationId', '');
                                formik.setFieldValue('platformServiceCorporationId', '');
                                return;
                            }
                            if (option?.corporationFunction === 'services') {
                                formik.setFieldValue('servicesCorporationId', selected.id);
                                formik.setFieldValue('organisingCorporationId', '');
                                formik.setFieldValue('providingCorporationId', '');
                                formik.setFieldValue('platformServiceCorporationId', '');
                            } else {
                                formik.setFieldValue('organisingCorporationId', selected.id);
                                formik.setFieldValue('servicesCorporationId', '');
                            }
                        }}
                        error={formik.errors.organisingCorporationId || formik.errors.servicesCorporationId}
                        minInputLength={formik.values.commercialNetworkId ? 0 : 2}
                        containerClassName="col-span-12 md:col-span-4"
                        isDisabled={organisingCorporationLocked || !formik.values.commercialNetworkId}
                    />

                    {userCan('admin_all_commercial_network_contracts', 'commercial_network') && (
                        <CustomSelectApiHookForm
                            key={formik.values.organisingCorporationId + 'providing-key'}
                            id="providingCorporationId"
                            label={<span>Empresa Impartidora {(formik.values.saveAsContract && formik.values.productTypeId === '26a41eb6-a9d1-4d2f-88e5-1bb59bdb9ae1' && !formik.values.servicesCorporationId) && <span className="text-red-500">*</span>}</span>}
                            formik={formik}
                            fetchOptions={async (searchText: string | null) => {
                                const organisingId = formik.values.organisingCorporationId;
                                if (!organisingId) return [];
                                return fetchProvidingCorporations({
                                    search_text: searchText,
                                    organising_corporation_id: organisingId,
                                }) as any;
                            }}
                            error={formik.errors.providingCorporationId}
                            minInputLength={0}
                            containerClassName="col-span-12 md:col-span-4"
                            isDisabled={!formik.values.organisingCorporationId || !!formik.values.servicesCorporationId}
                        />
                    )}

                    {userCan('admin_all_commercial_network_contracts', 'commercial_network') && (
                        <CustomSelectApiHookForm
                            key={formik.values.providingCorporationId + 'platform-key'}
                            id="platformServiceCorporationId"
                            label={<span>Empresa Plataforma</span>}
                            formik={formik}
                            fetchOptions={async (searchText: string | null) => {
                                const providingId = formik.values.providingCorporationId;
                                if (!providingId) return [];

                                return fetchPlatformCorporations({
                                    search_text: searchText,
                                    parent_corporation_id: providingId,
                                }) as any;
                            }}
                            error={formik.errors.platformServiceCorporationId}
                            minInputLength={0}
                            containerClassName="col-span-12 md:col-span-4"
                            isDisabled={!formik.values.providingCorporationId || !!formik.values.servicesCorporationId}
                        />
                    )}

                    {userCan('modify_contract_end_date', 'contracts') && (
                        <RequiredInput
                            id='draftExpirationDate'
                            required={false}
                            type="date"
                            label="Fecha de Expiración del Borrador"
                            className="col-span-12 md:col-span-4"
                            formik={formik}
                        />
                    )}

                    {!isCreateMode && (
                        <>
                            <RequiredInput
                                id='productStartDate'
                                required={formik.values.saveAsContract}
                                type="date"
                                label="Fecha Inicio del Curso"
                                className="col-span-12 md:col-span-4"
                                formik={formik}
                            />

                            <RequiredInput
                                id='productEndDate'
                                required={false}
                                type="date"
                                label="Fecha Fin del Curso"
                                className="col-span-12 md:col-span-4"
                                formik={formik}
                            />
                        </>
                    )}

                    <RequiredInput
                        id='operatorName'
                        type="text"
                        label="Nombre del operador"
                        className="col-span-12 md:col-span-4"
                        formik={formik}
                        required={false}
                    />

                    <RequiredInput
                        id='userLogin'
                        type="text"
                        label="Login del Creador"
                        className="col-span-12 md:col-span-4"
                        formik={formik}
                        required={false}
                    />

                    <RequiredInput
                        id='activationDate'
                        type="date"
                        label="Fecha de Activación"
                        className="col-span-12 md:col-span-4"
                        formik={formik}
                        required={formik.values.saveAsContract}
                        error={formik.errors.activationDate}
                    />

                    <RequiredInput
                        id='socialInsuranceAmount'
                        type="number"
                        label="Cuota de Seguridad Social"
                        className="col-span-12 md:col-span-4"
                        formik={formik}
                        required={false}
                    />

                    <CustomSelect
                        isSearchable
                        isClearable
                        required
                        id={'paymentMethodsId'}
                        label="Método de Pago"
                        options={getPaymentMethodsList()}
                        onChange={(e: any) => formik.setFieldValue('paymentMethodsId', e ? e.value : '')}
                        value={formik.values.paymentMethodsId ? { value: formik.values.paymentMethodsId, label: getPaymentMethodsList().find((c: any) => c.value === formik.values.paymentMethodsId)?.label } : null}
                        error={formik.errors.paymentMethodsId}
                        containerClassName="col-span-12 md:col-span-4"
                    />

                    <CustomSelect
                        isSearchable
                        isClearable
                        id={'expirationTypeId'}
                        label="Tipo de Vencimiento"
                        required={false}
                        options={getExpirationTypesList()}
                        onChange={(e: any) => {
                            const paymentInstalmentsNumber = e?.identifierLabel === "full_direct_debiting"
                                ? 1
                                : e?.identifierLabel === "credit_balance"
                                    ? 2
                                    : e?.identifierLabel === "instalment_payment"
                                        ? 2
                                        : 1;
                            formik.setValues({ ...formik.values, expirationTypeId: e ? e.value : '', paymentInstalmentsNumber: paymentInstalmentsNumber });
                            setExpirationType(e ? e.identifierLabel : null);
                        }}
                        value={formik.values.expirationTypeId ? { value: formik.values.expirationTypeId, label: getExpirationTypesList().find((c: any) => c.value === formik.values.expirationTypeId)?.label } : null}
                        error={formik.errors.expirationTypeId}
                        containerClassName="col-span-12 md:col-span-4"
                    />

                    <RequiredInput
                        key={formik.values.expirationTypeId}
                        id='paymentInstalmentsNumber'
                        type="number"
                        label="Nº Plazos de Pago"
                        className="col-span-12 md:col-span-4"
                        formik={formik}
                        required={false}
                        // disabled={expirationType !== "instalment_payment"}
                    />

                    <Textarea
                        id='tutorComment'
                        label="Observaciones del Tutor"
                        className="col-span-12 md:col-span-6"
                        value={formik.values.tutorComment}
                        onChange={(e) => formik.setFieldValue('tutorComment', e.target.value)}
                        required={false}
                        rows={4}
                        error={formik.errors.tutorComment}
                    />

                    <Textarea
                        id='saleComment'
                        label="Observaciones de la Venta"
                        className="col-span-12 md:col-span-6"
                        value={formik.values.saleComment}
                        onChange={(e) => formik.setFieldValue('saleComment', e.target.value)}
                        required={false}
                        rows={4}
                        error={formik.errors.saleComment}
                    />
                </div>
            </FormGroup>
        </div>
    );
}

export default ContractDataForm;
